import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppConfigBoundary } from "./config/RuntimeConfigContext.jsx";
import { SessionProvider } from "./auth/SessionContext.jsx";
import RouteGuard from "./shell/RouteGuard.jsx";
import { BackofficeIdentityProvider } from "./hierarchy/BackofficeIdentityContext.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import RootRedirect from "./pages/RootRedirect.jsx";
import NotFoundPage from "./pages/NotFoundPage.jsx";
import PostLoginResetNotice from "./pages/PostLoginResetNotice.jsx";
import BackofficeHomePage from "./hierarchy/BackofficeHomePage.jsx";
import SuperAgentsOverviewPage from "./hierarchy/SuperAgentsOverviewPage.jsx";
import SuperAgentLineOrgChartPage from "./hierarchy/SuperAgentLineOrgChartPage.jsx";
import SuperAgentPlcDetailPage from "./hierarchy/SuperAgentPlcDetailPage.jsx";
import SuperAgentAgentDetailPage from "./hierarchy/SuperAgentAgentDetailPage.jsx";
import PlcsListPage from "./workerLine/PlcsListPage.jsx";
import AgentsListPage from "./workerLine/AgentsListPage.jsx";
import WorkerLineOrgChartPage from "./workerLine/WorkerLineOrgChartPage.jsx";
import AgentInviteCardsPage from "./agent/AgentInviteCardsPage.jsx";
import AgentEmployerInviteCardsPage from "./agent/AgentEmployerInviteCardsPage.jsx";
import AgentOrgChartPage from "./agent/AgentOrgChartPage.jsx";
import AgentWorkersPage from "./agent/AgentWorkersPage.jsx";
import AgentSosAbnormalPage from "./agent/AgentSosAbnormalPage.jsx";
import AgentFootstepsPage from "./agent/AgentFootstepsPage.jsx";
import AgentZonesPage from "./agent/AgentZonesPage.jsx";
import AgentContactsPage from "./agent/AgentContactsPage.jsx";
import AgentPortalGuardState from "./agent/AgentPortalGuardState.jsx";
import { useAgentPortalGuard } from "./agent/useAgentPortalGuard.js";
import BuddyGuaranteesPage from "./agent/buddyGuarantees/BuddyGuaranteesPage.jsx";
import PublicInviteRegisterPage from "./public/PublicInviteRegisterPage.jsx";
import PublicEmployerRegisterPage from "./public/PublicEmployerRegisterPage.jsx";
import PublicWorkerResumePage from "./public/PublicWorkerResumePage.jsx";
import PublicPartnerRegisterPage from "./public/PublicPartnerRegisterPage.jsx";
import PartnerInviteCardsPage from "./partnerInvites/PartnerInviteCardsPage.jsx";
import WorkerApplicationInboxPage from "./workerApplications/WorkerApplicationInboxPage.jsx";
import EkycReviewQueuePage from "./ekycReviews/EkycReviewQueuePage.jsx";
import ReadyForMatchPage from "./readyForMatch/ReadyForMatchPage.jsx";
import WorkerProfilesPage from "./workerProfiles/WorkerProfilesPage.jsx";
import ThailandJobsEmployersPage from "./thailandJobs/ThailandJobsEmployersPage.jsx";
import ThailandJobsJobsPage from "./thailandJobs/ThailandJobsJobsPage.jsx";
import MatchingWorkspacePage from "./matchingWorkspace/MatchingWorkspacePage.jsx";
import MatchingRecommendationPage from "./matchingRecommendation/MatchingRecommendationPage.jsx";
import AssignmentsPage from "./assignments/AssignmentsPage.jsx";
import AssignmentOnboardingPage from "./onboarding/AssignmentOnboardingPage.jsx";
import LatestTrackingPage from "./tracking/LatestTrackingPage.jsx";
import FootstepHistoryPage from "./footsteps/FootstepHistoryPage.jsx";
import AbnormalHeartbeatPage from "./abnormal/AbnormalHeartbeatPage.jsx";
import GeofenceZonesPage from "./geofence/GeofenceZonesPage.jsx";
import FakeLocationReviewPage from "./fakeLocation/FakeLocationReviewPage.jsx";
import FaceCheckDailyPage from "./faceCheck/FaceCheckDailyPage.jsx";
import SosCasesPage from "./sos/SosCasesPage.jsx";
import WarRoomPage from "./warRoom/WarRoomPage.jsx";
import ChatInboxPage from "./chat/ChatInboxPage.jsx";

function Guarded({ children }) {
  return (
    <RouteGuard>
      <BackofficeIdentityProvider>{children}</BackofficeIdentityProvider>
    </RouteGuard>
  );
}

function AgentPortalRouteBoundary({ children }) {
  const guard = useAgentPortalGuard();

  if (guard.status === "forbidden") {
    return <Navigate to="/backoffice" replace />;
  }

  if (guard.status !== "ready") {
    return <AgentPortalGuardState crumbs={["Backoffice", "Agent"]} guard={guard} />;
  }

  return children;
}

function AgentGuarded({ children }) {
  return (
    <Guarded>
      <AgentPortalRouteBoundary>{children}</AgentPortalRouteBoundary>
    </Guarded>
  );
}

export default function App() {
  return (
    <AppConfigBoundary>
      <SessionProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />

            {/*
              P092: the public, unauthenticated worker-facing Invite Card
              register page (W39 reference) - opened from an Agent's Invite
              Card link, not part of the backoffice at all. Deliberately
              NOT wrapped in Guarded/RouteGuard/AppShell: this route must
              never require a session token, must never redirect to
              /login, and must never render the sidebar. See
              src/public/publicInviteApi.js for why it also can't reuse the
              authenticated apiClient.
            */}
            <Route path="/register" element={<PublicInviteRegisterPage />} />
            {/* P156: the employer's own half of onboarding. A separate public
                route from /register on purpose - PublicEmployerInviteBaseUrl
                points here, and the two invite links must never resolve to
                each other's form. */}
            <Route path="/employer-register" element={<PublicEmployerRegisterPage />} />
            {/* P167: the third public invite family - a Super Agent's letter to
                a licensed recruitment agency abroad. Its own route for the same
                reason /employer-register is separate from /register:
                PublicPartnerInviteBaseUrl points here, and the three invite
                links must never resolve to each other's form. */}
            <Route path="/partner-register" element={<PublicPartnerRegisterPage />} />
            {/* The employer's own resume link. Unauthenticated like the two
                routes above it, and for the same reason: the employer has no
                account here and this flow does not create one. The token in the
                query string is the entire credential, so this route must never
                sit behind <Guarded> - an operator's session must not be what
                lets an employer's decision through. */}
            <Route path="/worker-resume" element={<PublicWorkerResumePage />} />

            <Route path="/backoffice" element={<Guarded><BackofficeHomePage /></Guarded>} />

            {/*
              P085 built the Super Agents overview/list. P086 replaces the
              P085 "next packet" reserved notice with the real Super Agent
              detail + Worker Line Org Chart screen.
            */}
            <Route path="/backoffice/super-agents" element={<Guarded><SuperAgentsOverviewPage /></Guarded>} />
            {/*
              P167: the Super Agent's own Invite Card screen - the letter they
              send to a licensed recruitment agency abroad inviting it to join
              their line as an Agent. Not nested under /super-agents/:id: a Super
              Agent's own line comes from their access scope (never a route
              param, the same rule the Agent Portal's invite screen follows), and
              a SuperAdmin picks a line on the page itself.
            */}
            <Route path="/backoffice/partner-invite-cards" element={<Guarded><PartnerInviteCardsPage /></Guarded>} />
            <Route
              path="/backoffice/super-agents/:superAgentId"
              element={<Guarded><SuperAgentLineOrgChartPage /></Guarded>}
            />
            {/*
              P087: the next drill-down level after a Super Agent line - the
              PLC/บมจ home page. Reachable from the org chart's PLC drawer
              action link. Agent detail is intentionally not built yet.
            */}
            <Route
              path="/backoffice/super-agents/:superAgentId/plcs/:plcId"
              element={<Guarded><SuperAgentPlcDetailPage /></Guarded>}
            />
            {/*
              P088: the deepest level of the worker-intake drill-down chain
              so far - the Agent detail page. Agent is the only level where
              Invite Card creation belongs; it stays disabled here because
              the real Invite Card API (Prompt 089R) restricts creation to
              the owning Agent's own account, not SuperAdmin browsing this
              drill-down view (see SuperAgentAgentDetailPage.jsx's own
              "Create Invite Card" button note, updated in the Prompt 134
              acceptance audit for accuracy).
            */}
            <Route
              path="/backoffice/super-agents/:superAgentId/plcs/:plcId/agents/:agentId"
              element={<Guarded><SuperAgentAgentDetailPage /></Guarded>}
            />

            {/*
              P132: Worker Line Admin CRUD UI over the Prompt 131 backend
              (GET/POST/PATCH /api/backoffice/worker-line/plcs|agents[/{id}]).
              PlcsListPage/AgentsListPage are new standalone, paginated,
              filterable list+create+edit+detail screens (distinct from the
              read-only, tree-derived per-Super-Agent drill-down pages
              above, which are unchanged) - reachable from the sidebar's
              WORKER LINE group and from a parent's own detail drawer "next
              actions" link (?createForSuperAgentId=...[&createForPlcId=...]
              auto-opens Create prefilled).
            */}
            {/*
              P161: the sysadmin menu no longer offers the mixed both-sides PLC
              list - it offers บริษัทจัดหางาน (ฝั่งงาน) and บริษัทจัดหาแรงงาน
              (ฝั่งแรงงาน) as two separate screens. Same component, told which
              side it is showing; the side rides to the API as a real
              organizationSide filter. The unfiltered /plcs route below stays
              registered because a Super Agent detail's own "สร้าง ... ใต้สายงาน
              นี้" link and the Org Chart's ghost nodes both point at it with a
              parent id - those must keep working whichever side that parent is
              on, and existing bookmarks must not 404.
            */}
            <Route path="/backoffice/worker-line/plcs/job-side" element={<Guarded><PlcsListPage side="JobDemand" /></Guarded>} />
            <Route path="/backoffice/worker-line/plcs/worker-side" element={<Guarded><PlcsListPage side="WorkerSupply" /></Guarded>} />
            <Route path="/backoffice/worker-line/plcs" element={<Guarded><PlcsListPage /></Guarded>} />
            <Route path="/backoffice/worker-line/agents" element={<Guarded><AgentsListPage /></Guarded>} />

            {/*
              P132/P137: the platform-wide Worker Line Org Chart (SuperAdmin
              -> Super Agent -> PLC -> Agent) - a polished command-board
              network graph (Prompt 137 enterprise redesign) composed from
              the three real Worker Line Admin list endpoints
              (GET .../worker-line/super-agents|plcs|agents) via
              ./workerLine/useWorkerLineOrgChart.js. Every node's drawer
              fetches the real Worker Line Admin detail and offers a real
              Edit action (the Agent drawer also carries Prompt 136's login-
              account status/Reset Password action) - never a static/
              reserved chart.
            */}
            <Route path="/backoffice/worker-line/org-chart" element={<Guarded><WorkerLineOrgChartPage /></Guarded>} />

            {/*
              P090: the Agent Portal's own Invite Card management screen
              (W43 reference) - the only route where Invite Card creation is
              actually enabled. agentId is derived from the signed-in
              actor's own accessScopes, never a route param, so this is
              reachable only by the owning Agent; anyone else sees a
              reserved/forbidden state and the invite card API is never
              called for them.
            */}
            {/* P156: the job-side Agent's Invite Card screen - the employer
                fills the form in themselves, and the submission shows on this
                Agent's Org Chart as pending. */}
            <Route path="/backoffice/agent/employer-invite-cards" element={<Guarded><AgentEmployerInviteCardsPage /></Guarded>} />
            <Route path="/backoffice/agent/invite-cards" element={<AgentGuarded><AgentInviteCardsPage /></AgentGuarded>} />

            {/*
              P138W-R2: the real, traceable "Quick Registered" applications
              list - fixes a live bug where the Agent Dashboard's funnel
              showed a real "Quick Registered" count with no way to see who
              those applications actually were, and the Workers page just
              said "no workers" with no explanation that Quick Registered
              applications aren't Worker Profiles yet. Reuses the exact same
              Prompt 093 GET .../worker-applications (list) and
              GET .../worker-applications/{id} (detail) endpoints
              WorkerApplicationInboxPage.jsx already calls for SuperAdmin -
              no new backend endpoint. Every "Quick Registered" number
              elsewhere in the Agent Portal (Dashboard funnel, Org Chart,
              Invite Card's own metric tile) links here.
            */}
            {/* P157: Applications was merged into Workers. A Quick Registered
                applicant lives in the Workers "รอ e-KYC" tab, so this URL now
                redirects there - the page is gone, only the deep link survives
                (the dashboard/org-chart still link here). */}
            <Route path="/backoffice/agent/applications" element={<Navigate to="/backoffice/agent/workers" replace />} />

            {/*
              P138R fixed a live bug where an Agent-role session at bare
              /backoffice still rendered a hardcoded "SuperAdmin Dashboard".
              P138X went further: BackofficeHomePage.jsx (bare /backoffice)
              is now the single, role-aware dashboard for BOTH SuperAdmin and
              Agent sessions - there is no separate Agent dashboard page
              component anymore. This route only exists so old bookmarks/
              deep links to the legacy P138R URL still land somewhere real
              instead of 404ing; it is a pure redirect to the canonical
              /backoffice route, still behind Guarded so an unauthenticated
              visitor is sent to /login first like every other backoffice
              route. navConfig.js's own agent-dashboard sidebar item now
              links straight at /backoffice and only keeps this path in its
              activeMatch list for the brief moment a visitor is still on it.
            */}
            <Route path="/backoffice/agent/dashboard" element={<Guarded><Navigate to="/backoffice" replace /></Guarded>} />

            {/*
              P138: the rest of the Agent Portal - six new Agent-scoped
              screens under src/agent/, each reusing an existing backend API
              (never a new backend route) behind a leaner, Agent-scoped page
              component (never the SuperAdmin page directly, since those
              hardcode "SuperAdmin" copy/cross-org filters). Every page
              guards itself with useAgentPortalGuard() exactly like
              AgentInviteCardsPage above - agentId always comes from the
              signed-in actor's own accessScopes (findOwningAgentId), never a
              route param, and no page ever sends an explicit agentId filter
              to a list endpoint - all scoping is the backend's own session
              scope. See src/shell/navConfig.js's AGENT_NAV_SECTIONS doc
              comment for the per-item backend-reuse mapping and the
              confirmed Audit Logs read-endpoint gap that keeps that one item
              reserved.
            */}
            <Route path="/backoffice/agent/org-chart" element={<AgentGuarded><AgentOrgChartPage /></AgentGuarded>} />
            <Route path="/backoffice/agent/workers" element={<AgentGuarded><AgentWorkersPage /></AgentGuarded>} />
            <Route path="/backoffice/agent/sos-abnormal" element={<AgentGuarded><AgentSosAbnormalPage /></AgentGuarded>} />
            <Route path="/backoffice/agent/footsteps" element={<AgentGuarded><AgentFootstepsPage /></AgentGuarded>} />
            <Route path="/backoffice/agent/zones" element={<AgentGuarded><AgentZonesPage /></AgentGuarded>} />
            <Route path="/backoffice/agent/contacts" element={<AgentGuarded><AgentContactsPage /></AgentGuarded>} />

            {/*
              P140B: Buddy Guarantee - the sidebar item stayed reservedNote
              through Prompt 138 (never in that packet's scope, see
              agentPortalRoutes.test.jsx's old proof, now updated). P140A
              built the real backend (dbo.WmxBuddyGuarantees + the
              api/backoffice/agent/buddy-guarantees CRUD API); this route is
              the first real caller of it. Reuses the existing worker-
              applications/worker-profiles list endpoints only for the
              create flow's source-person search - no new backend route.
            */}
            <Route path="/backoffice/agent/buddy-guarantees" element={<AgentGuarded><BuddyGuaranteesPage /></AgentGuarded>} />

            {/*
              P094B: the Worker Application Inbox (W44 reference) - the first
              real screen under the WORKER INTAKE group's Invite / Applications
              item. Read-only over the Prompt 093 API: lists/views Quick
              Register applications that came from an Invite Card, before
              e-KYC. Must be declared before the /backoffice/worker-intake/*
              reset catch-all below so this specific path wins over it.
            */}
            {/*
              P162: the sysadmin Workers screen. The owner asked for the WORKER
              INTAKE menu to go and to be replaced by the Agent Portal's own
              Workers screen, "ให้เหมือนทุกอย่างแต่ให้ข้อมูล scope เป็น sysadmin".

              So this is the SAME component as /backoffice/agent/workers, told
              which audience it is serving - not a copy. Scope is not decided
              here: every list on that page resolves visibility server-side from
              the caller's session, so a Platform-scoped caller sees the whole
              platform for the same reason an Agent sees one line.

              The four /backoffice/worker-intake/* routes below stay registered
              on purpose (the standing fold-into-parent convention): existing
              bookmarks, audit-log links and the Dashboard's own drill-downs all
              still resolve. Only the menu entries are gone.
            */}
            <Route path="/backoffice/workers" element={<Guarded><AgentWorkersPage variant="platform" /></Guarded>} />

            <Route path="/backoffice/worker-intake/invite-applications" element={<Guarded><WorkerApplicationInboxPage /></Guarded>} />

            {/*
              P096B: the e-KYC Review Queue (W45 reference) - the second real
              screen under the WORKER INTAKE group. Reviews e-KYC document
              submissions from workers already past Quick Register (Prompt
              093/094B) via the Prompt 095/095R e-KYC review API, including
              the SuperAdmin decision action. Must be declared before the
              /backoffice/worker-intake/* reset catch-all below so this
              specific path wins over it.
            */}
            <Route path="/backoffice/worker-intake/ekyc-review" element={<Guarded><EkycReviewQueuePage /></Guarded>} />

            {/*
              P098B: the Worker Profiles inbox (W46 reference) - the third
              real screen under the WORKER INTAKE group. Read-only over the
              Prompt 097/097R Worker Profile API: lists/views Worker Profiles
              that were already created from an approved e-KYC review (a
              separate action on the e-KYC Review Queue screen, not exposed
              here). Must be declared before the /backoffice/worker-intake/*
              reset catch-all below so this specific path wins over it.
            */}
            <Route path="/backoffice/worker-intake/worker-profiles" element={<Guarded><WorkerProfilesPage /></Guarded>} />

            {/*
              P100B: the Ready For Match queue (W47 reference) - the fourth
              real screen under the WORKER INTAKE group. Reviews Worker
              Profiles (Prompt 097) that are not yet ReadyForMatch and marks
              one ready over the Prompt 099 Ready For Match API. Ready For
              Match is a readiness-flag update only - it never assigns an
              employer/job, never creates a WorkerAssignment row, and never
              starts tracking/device binding (none of those exist anywhere in
              this codebase yet). Must be declared before the
              /backoffice/worker-intake/* reset catch-all below so this
              specific path wins over it.
            */}
            <Route path="/backoffice/worker-intake/ready-for-match" element={<Guarded><ReadyForMatchPage /></Guarded>} />

            {/*
              P102B: Thailand Jobs > Employers (W48 reference) - the first
              real screen under the THAILAND JOBS group. Read-heavy list +
              detail over the Prompt 101 Employers/Jobs API:
              lists/views employers, and creates employer/job demand
              records. This is job-supply-side data intake only - it never
              calls a matching/assignment/tracking/Invite-Card API (none of
              those exist for this side of the platform), and never accepts
              or displays a worker id anywhere.
            */}
            <Route path="/backoffice/thailand-jobs/employers" element={<Guarded><ThailandJobsEmployersPage /></Guarded>} />

            {/*
              P103B: Thailand Jobs > Jobs (W49 reference) - the second real
              screen under the THAILAND JOBS group. Read-heavy list + detail
              over the Prompt 101 Jobs API: lists/views jobs, creates/edits
              job demand records, and marks a job Ready For Matching (a
              real, already-implemented status change). This is
              job-supply-side data intake only - it never calls a
              matching/assignment/tracking/Invite-Card API (none of those
              exist for this side of the platform), and never accepts or
              displays a worker id anywhere.
            */}
            <Route path="/backoffice/thailand-jobs/jobs" element={<Guarded><ThailandJobsJobsPage /></Guarded>} />


            {/*
              P107: Thailand Jobs > Matching Queue (W51 reference) - the
              fourth real screen under the THAILAND JOBS group. Entirely
              read-only over the Prompt 106 Matching Queue API: lists jobs
              already ReadyForMatching, shows one job's detail (skill
              requirements + candidate-match summary), and shows one job's
              ready-and-visible candidates with their deterministic match
              score. This screen never calls a matching/assignment/
              tracking/device/geofence/Invite-Card API (none of those exist
              anywhere in this codebase), and never accepts or displays a
              worker id as a write - "สร้าง Assignment" is always a disabled
              button with a fixed reserved-reason string.
            */}
            <Route path="/backoffice/thailand-jobs/matching-queue" element={<Guarded><MatchingWorkspacePage /></Guarded>} />

            {/*
              P146-REV1: Thailand Jobs > Matching Recommendation - the
              admin-owned, employer-aware-only decision console. Reads the
              Prompt 106 ready-jobs route for its job queue and the new
              P146-REV1 routes (GET .../matching-recommendations/jobs/{jobId}/candidates,
              POST .../matching-recommendations, GET .../matching-recommendations)
              for scored candidates and the one write this screen performs.
              An Admin/SysAdmin - or a Super Agent/PLC accountable for the
              branch, per the P145 delegated-authority rule - approves a
              worker so the employer can be informed. There is deliberately no
              employer accept/reject/negotiation control anywhere on this
              screen or in the API behind it: the employer is a recipient of
              information, never a participant. Approving never creates a
              WorkerAssignment (still unbuilt) and never claims the employer
              was notified - no notification sender exists in this codebase,
              so the recorded state stays "รอระบบแจ้งนายจ้าง".
            */}
            <Route path="/backoffice/thailand-jobs/matching-recommendation" element={<Guarded><MatchingWorkspacePage /></Guarded>} />

            {/*
              P109B: Thailand Jobs > Assignments (W52 reference) - the fifth
              real screen under the THAILAND JOBS group. Read-heavy list +
              detail over the Prompt 108 Worker Assignment API, plus a real
              Create Assignment and Cancel Assignment flow. This is the only
              screen in the whole frontend where a workerId is ever accepted
              as a write (the Create Assignment drawer's own "ผู้สมัคร"
              field) - it is never accepted by any job create/edit form.
              This screen never calls a tracking/device/geofence/Invite-Card
              API (none of those exist anywhere in this codebase), and
              cancelling an assignment is always a status change (Active ->
              Cancelled), never a delete.
            */}
            {/* P156: Final Approval's own URL. Without it the tab navigated
                to the Ready Pool path and bounced straight back to tab 1. */}
            <Route path="/backoffice/thailand-jobs/matching-approved" element={<Guarded><MatchingWorkspacePage /></Guarded>} />
            <Route path="/backoffice/thailand-jobs/assignments" element={<Guarded><MatchingWorkspacePage /></Guarded>} />

            {/*
              P111B: Assignment Onboarding / Device / SIM Binding (W53
              reference) - reachable only from an assignment's own detail
              drawer ("เปิด Onboarding" on /backoffice/thailand-jobs/assignments
              above), never from the sidebar and never as a list - the real
              Prompt 110 API has only an assignment-scoped detail endpoint
              (GET .../assignments/{assignmentId}/onboarding), never a
              cross-assignment list/summary endpoint, so this route is not
              linked from anywhere except one specific assignment's own
              detail. This screen never calls a heartbeat/location/
              tracking/geofence/face-recognition API (none of those exist
              anywhere in this codebase), and canStartTracking is always
              rendered blocked.
            */}
            <Route
              path="/backoffice/thailand-jobs/assignments/:assignmentId/onboarding"
              element={<Guarded><AssignmentOnboardingPage /></Guarded>}
            />

            {/*
              P113B: Backoffice Latest Tracking (W54 reference) - entirely
              read-only over the Prompt 112 Backoffice Latest Tracking API
              (GET .../tracking/workers/latest, GET .../tracking/workers/
              {workerId}/latest): a paginated, scope-isolated list of every
              visible worker's latest heartbeat/location snapshot, plus a
              single-worker detail. The sidebar's "Footstep" item now links
              here - the existing IA has no dedicated "Tracking Monitor" item
              yet, so this route reuses the nearest existing Command-group
              item while this page's own title ("Latest Tracking Status",
              never "Footstep") and permanent top banner make clear this is
              latest-status only, not Footstep history. This screen never
              calls a mobile ingestion/geofence/fake-location/face-
              recognition/SOS API (none of those exist anywhere in this
              codebase), never renders a map, and canViewFootstepHistory is
              always rendered blocked with the fixed reserved-reason string.
            */}
            <Route path="/backoffice/tracking/latest" element={<Guarded><LatestTrackingPage /></Guarded>} />

            {/*
              P115B: Backoffice Footstep History (W55 reference) - an
              on-demand, purpose-logged view of a worker's historical
              dbo.WmxLocationEvents path over a chosen time range (Prompt 114
              API: GET .../tracking/workers/{workerId}/footsteps, GET
              .../footsteps/summary), plus a SuperAdmin-only compliance
              review of who has viewed footstep history (GET
              .../tracking/footstep-access-logs). The sidebar's "Footstep"
              item now links here instead of /backoffice/tracking/latest -
              this is the real Footstep History screen the label always
              implied; /backoffice/tracking/latest (P113B) stays mounted and
              reachable by direct URL, matching the existing precedent of
              routes reachable only outside the sidebar (e.g. Assignment
              Onboarding). This screen never evaluates a geofence, never
              scores a location as fake, never touches face recognition or
              SOS, never creates an abnormal event, and never calls an
              export API - canExport/canEvaluateGeofence are always rendered
              blocked with the backend's own fixed reserved-reason strings.
            */}
            <Route path="/backoffice/tracking/footsteps" element={<Guarded><FootstepHistoryPage /></Guarded>} />

            {/*
              P117B: Backoffice Abnormal Heartbeat (W56 reference) - the
              sidebar's own "Missing Heartbeat" item links here (P144-R split
              the old combined "SOS / Abnormal" item into this and a separate
              "SOS Cases" item). Entirely over the Prompt 116 Backoffice
              Abnormal Heartbeat API: a paginated, scope-isolated list of
              MissingHeartbeat abnormal events
              (GET .../abnormal/events), a single-event detail with prepared
              recipients (GET .../abnormal/events/{abnormalEventId}), a
              SuperAdmin-only manual scan (POST .../abnormal/heartbeat-scan),
              and acknowledge/resolve
              (POST .../abnormal/events/{abnormalEventId}/acknowledge|resolve).
              This screen never calls a geofence/fake-location/face-
              recognition/SOS API (none of those exist anywhere in this
              codebase), never scans event tables (the backend's own scan
              reads dbo.WmxWorkerLatestStatus only), and never sends a real
              notification - notificationDeliveryStarted is always rendered
              false with the backend's own fixed reserved-reason string.
            */}
            <Route path="/backoffice/abnormal/heartbeat" element={<Guarded><AbnormalHeartbeatPage /></Guarded>} />

            {/*
              P119B: Backoffice Geofence Zones (W57 reference) - a new
              "Geofence Zones" sidebar item under the COMMAND group links
              here. Entirely over the Prompt 118 Backoffice Geofence Zone
              API: a paginated, scope-isolated list of Circle zones
              (GET .../geofence/zones), a single-zone detail with an
              assigned-worker summary (GET .../geofence/zones/{zoneId}),
              Create Zone (POST .../geofence/zones), Edit Zone
              (PATCH .../geofence/zones/{zoneId}), Assign Workers
              (POST .../geofence/zones/{zoneId}/worker-assignments),
              worker-zone removal
              (POST .../worker-assignments/{workerId}/remove), and a manual
              evaluation of the latest known worker location against active
              zones (POST /api/backoffice/geofence/evaluate). This screen
              never calls a polygon/fake-location-scoring/face-recognition/
              SOS API (none of those exist anywhere in this codebase), never
              scans location event history (the backend's own evaluation
              reads dbo.WmxWorkerLatestStatus only), never renders a real
              map (the Circle visualization is a static SVG illustration
              built only from a zone's own centerLatitude/centerLongitude/
              radiusMeters), and never sends a real notification -
              notificationDeliveryStarted is always rendered false with the
              backend's own fixed reserved-reason string.
            */}
            <Route path="/backoffice/geofence/zones" element={<Guarded><GeofenceZonesPage /></Guarded>} />

            {/*
              P121B: Backoffice Fake Location Review (W58 reference) - a new
              "Fake Location Review" sidebar item under the COMMAND group
              links here. Entirely over the Prompt 120 Fake Location Signal
              API: a scoped, time-bounded, purpose-logged deterministic
              evaluation of a worker's stored dbo.WmxLocationEvents metadata
              (POST .../fake-location/evaluate), a paginated list of
              previously stored evaluations
              (GET .../fake-location/evaluations), and a single-evaluation
              detail (GET .../fake-location/evaluations/{evaluationId} - a
              real, implemented P120 route, not reserved/omitted). This
              screen never calls an external anti-fraud/geolocation service,
              never implies AI/ML (scoring is fixed, rule-based, and
              explainable), never evaluates a geofence (Prompt 118 owns
              that, entirely separately), never touches face recognition or
              SOS, never sends a real notification, and never implies
              automatic worker punishment/blocking - every result and every
              High-risk state carries a fixed warning that a score is a
              signal for human review only. No coordinate/points list is
              ever requested, stored, or rendered anywhere on this page.
            */}
            <Route path="/backoffice/tracking/fake-location" element={<Guarded><FakeLocationReviewPage /></Guarded>} />

            {/*
              P123B: Backoffice Daily Face Check (W59 reference) - a new
              "Face Check" sidebar item under the COMMAND group links here,
              right after Fake Location Review. Entirely over the Prompt 122
              Daily Face Check API: a paginated, scope-isolated list of
              dbo.WmxFaceCheckEvents rows (GET .../face-check/daily), a
              single-event detail (GET .../face-check/daily/
              {faceCheckEventId}), a review decision
              (POST .../face-check/daily/{faceCheckEventId}/review), and a
              SuperAdmin-only missing-scan
              (POST .../face-check/daily/missing-scan). This screen never
              performs real biometric face recognition and never calls an
              external face-recognition provider - biometricVerificationPerformed
              is always rendered false with the backend's own fixed
              reserved-reason string. It never stores/shows a raw face image
              or a raw storage key (only hasFaceCaptureReference), never
              implements SOS, never sends a real notification
              (notificationDeliveryStarted is always rendered false), and
              never implies automatic worker punishment/blocking - a review
              decision only ever updates complianceStatus for human
              record-keeping, and a missing-scan run only ever creates an
              Open FaceCheckMissing abnormal event for human review. Precise
              latitude/longitude are intentionally never rendered here even
              though FaceCheckDailyDetailDto carries them.
            */}
            <Route path="/backoffice/face-check/daily" element={<Guarded><FaceCheckDailyPage /></Guarded>} />

            {/*
              P125B: Backoffice SOS Cases (W60 reference) - the existing "SOS
              / Abnormal" sidebar item now also highlights active on this
              route (its own link target is unchanged, see navConfig.js/
              Sidebar.jsx); this route itself is reachable only by direct
              URL, the same precedent as Assignment Onboarding/the old
              Latest Tracking route. Entirely over the Prompt 124 Backoffice
              SOS Cases API: a paginated, scope-isolated list of
              dbo.WmxSupportCases rows with CaseType = Sos
              (GET .../sos/cases), a single-case detail with its event
              timeline, prepared recipients, and location snapshot
              (GET .../sos/cases/{supportCaseId}), and acknowledge/resolve
              (POST .../sos/cases/{supportCaseId}/acknowledge|resolve). This
              screen never sends a real notification
              (notificationDeliveryStarted is always rendered false with the
              backend's own fixed reserved-reason string), never calls an
              external emergency-service integration, and never runs a
              medical/legal advice engine - sosType is a plain worker-picked
              category label, nothing more. There is no notification-send
              button, no external-emergency-service action, and no advice
              panel anywhere on this page.
            */}
            <Route path="/backoffice/sos/cases" element={<Guarded><SosCasesPage /></Guarded>} />

            {/*
              P135: War Room (W61 reference) - a real, functional command
              board, not a reserved sidebar item. Deliberately introduces no
              new backend endpoint: it calls the exact same Prompt 112
              Backoffice Latest Tracking read API
              (GET .../tracking/workers/latest,
              GET .../tracking/workers/{workerId}/latest)
              ../tracking/LatestTrackingPage.jsx already calls, just
              presented as a seating-map board instead of a table. Never
              loads more than one server page (hard-capped at 100 rows) into
              the browser regardless of how many workers exist in the
              caller's scope, never renders a map (no map/tile API exists),
              never implies real push/SMS/LINE notification delivery, and
              never implies Wi-Fi/cellular fingerprint data (none of those
              exist anywhere in this codebase). Scope (SuperAdmin/SuperAgent/
              PLC/Agent) is resolved entirely server-side from the caller's
              session token - this route never receives or forwards a
              client-supplied scope id.
            */}
            <Route path="/backoffice/war-room" element={<Guarded><WarRoomPage /></Guarded>} />

            {/*
              P154: worker messages - the Agent's half of the real
              worker-to-Agent conversation the mobile app's
              lib/features/agent_chat/ screens write into. Entirely over the
              P154 Backoffice Chat API added by BackofficeChatController:
              GET .../chat/threads (the scope-isolated inbox),
              GET .../chat/threads/{threadId} (one conversation, whose read
              side-effect is what "opening it" means), and
              POST .../chat/threads/{threadId}/messages (the reply).

              This route mounts one page for both roles rather than a separate
              Agent-portal copy under src/agent/, the same precedent as War
              Room: the endpoint, the copy and the controls are identical for
              a SuperAdmin and an Agent, and the only difference between them
              - which conversations exist at all - is resolved server-side
              from the session token by ChatService.ResolveAgentScope. This
              route never receives or forwards a client-supplied Agent id.

              The screen renders no edit control and no delete control,
              because the backend deliberately exposes neither route: a worker
              writes here about pay, injuries and conditions, and the party
              those complaints are about must not be able to make them
              disappear. It never sends a real push/SMS/LINE notification and
              never attaches a file (the API carries a text body and nothing
              else - none of those exist anywhere in this codebase).
            */}
            <Route path="/backoffice/messages" element={<Guarded><ChatInboxPage /></Guarded>} />

            {/*
              P082/P083 worker-intake and job-supply org-chart/detail screens were
              rejected by Codex/owner as confusing IA. Those page components still
              exist on disk for recoverability, but are intentionally not imported
              here - any old deep link under these prefixes now shows a calm reset
              notice instead of the rejected UI. React Router's trailing "/*"
              matches the bare prefix itself and any depth below it.
            */}
            <Route path="/backoffice/worker-intake/*" element={<Guarded><PostLoginResetNotice /></Guarded>} />
            <Route path="/backoffice/job-supply/*" element={<Guarded><PostLoginResetNotice /></Guarded>} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </SessionProvider>
    </AppConfigBoundary>
  );
}
